/**
 * Thin client-side helpers for talking to the BFF proxy's /client-proxy/* endpoints.
 *
 * All authentication state lives in an httpOnly cookie managed by the proxy –
 * the browser never sees a raw token.
 */
import axios from 'axios';

import { CurrentUserDTO } from '../app/auth/dto';

interface SessionCheckResponse {
  authenticated: boolean;
  isImpersonating: boolean;
  /** Unix timestamp (seconds) when the impersonation token expires. Only present when isImpersonating is true. */
  impersonationExpiresAt?: number;
}

interface MeResponse {
  authenticated: boolean;
  user?: CurrentUserDTO;
}

/** Redirect the browser to the proxy's OIDC login endpoint. */
export function redirectToLogin(redirect?: string): void {
  const params = new URLSearchParams();
  if (redirect) params.set('redirect', redirect);
  const qs = params.toString();
  window.location.href = `/client-proxy/login${qs ? `?${qs}` : ''}`;
}

/** POST to the proxy to destroy the session and redirect to Keycloak logout. */
export function redirectToLogout(): void {
  // Use a form POST so the browser follows the redirect from the proxy.
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = '/client-proxy/logout';
  document.body.appendChild(form);
  form.submit();
}

/** Lightweight session check – no token parsing, just a boolean. */
export async function checkSession(): Promise<SessionCheckResponse | null> {
  try {
    const { data } = await axios.get<SessionCheckResponse>('/client-proxy/session', {
      withCredentials: true,
    });
    return data;
  } catch {
    return null;
  }
}

/** Fetch current user identity from the proxy's /client-proxy/me endpoint. */
export async function fetchCurrentUser(): Promise<CurrentUserDTO | undefined> {
  try {
    const { data } = await axios.get<MeResponse>('/client-proxy/me', { withCredentials: true });
    return data.authenticated ? data.user : undefined;
  } catch {
    return undefined;
  }
}
